<template>
    <BaseForm v-if="series">
        <template #fields>
            <TextField
                :value="values.nameShort"
                :label="$t('name-short')"
                :error-messages="errors.nameShort"
                :required="true"
                @input="setFieldValue('nameShort', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('nameShort', true)"
            />
            <TextField
                :value="values.nameLong"
                :label="$t('name-long')"
                :error-messages="errors.nameLong"
                :required="true"
                @input="setFieldValue('nameLong', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('nameLong', true)"
            />
            <TextAreaField
                :value="values.subtitle"
                :label="$t('subtitle')"
                :error-messages="errors.subtitle"
                :max-length="seriesIdentifiersSchema.shape.subtitle.maxLength ?? undefined"
                @input="setFieldValue('subtitle', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('subtitle', true)"
            />
            <MultiLink
                :label="$t('external-event-series-websites')"
                :links="values.websites"
                :error-messages="errors.websites"
                @input="setFieldValue('websites', $event)"
                @delete="setFieldValue('websites', $event)"
            />
            <TextField
                :value="values.numberKapman"
                :label="$t('number-kapman')"
                :error-messages="errors.numberKapman"
                :help-button-text="$t('number-kapman-help')"
                @input="setFieldValue('numberKapman', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('numberKapman', true)"
            />
            <TextField
                :value="values.numberShort"
                :label="$t('number-short')"
                :error-messages="errors.numberShort"
                :help-button-text="$t('number-short-help')"
                @input="setFieldValue('numberShort', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('numberShort', true)"
            />
            <NumberField
                :value="values.foundationYear"
                :label="$t('foundation-year')"
                :error-messages="errors.foundationYear"
                @input="setFieldValue('foundationYear', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('foundationYear', true)"
            />
            <TextAreaField
                :value="values.notes"
                :label="$t('notes')"
                :error-messages="errors.notes"
                :max-length="seriesIdentifiersSchema.shape.notes.maxLength ?? undefined"
                @input="setFieldValue('notes', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('notes', true)"
            />
            <TextAreaField
                :value="values.description"
                :label="$t('description')"
                :error-messages="errors.description"
                :max-length="seriesIdentifiersSchema.shape.description.maxLength ?? undefined"
                @input="setFieldValue('description', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('description', true)"
            />
            <FileUpload
                :label="$t('logo')"
                :url="iconUrl"
                :loading="fileUpload.loading.value"
                :accept="PROCESSABLE_IMAGE_MIME_TYPES.join(',')"
                :clear-button-text="$t('delete-series-icon')"
                @change="uploadIcon('iconUpload', $event)"
                @clear="deleteIcon('iconUpload')"
            />
        </template>
    </BaseForm>
</template>
<script setup lang="ts">
import BaseForm from '@/components/Form/BaseForm.vue';
import FileUpload from '@/components/Form/FileUpload.vue';
import NumberField from '@/components/Form/NumberField.vue';
import TextAreaField from '@/components/Form/TextAreaField.vue';
import TextField from '@/components/Form/TextField.vue';
import { useMultiLink } from '@/components/MultiLink/composables/useMultiLink';
import MultiLink from '@/components/MultiLink/MultiLink.vue';
import { useEventSeriesInjection } from '@/event/composables/useEventSeriesInjection';
import { useActiveCard } from '@/forms/composables/useActiveCard';
import { useForm } from '@/forms/composables/useForm';
import { PROCESSABLE_IMAGE_MIME_TYPES } from '@/upload/upload';
import { thumbnailUrl } from '@/utils/file';
import { computed } from 'vue';
import { getEventSeriesIdentifiersSchema } from '../schemas';

const props = defineProps<{ seriesId: string }>();

const seriesId = computed(() => props.seriesId);

const { series, updateEventSeries, createEventSeriesMultiLink, pendingLogoUrl } = useEventSeriesInjection();

const seriesIdentifiersSchema = computed(() => getEventSeriesIdentifiersSchema(seriesId.value));

const { resetActiveCard } = useActiveCard();
const { getFormFieldLinks, saveMultiLinkFields } = useMultiLink(createEventSeriesMultiLink);

const websitesId = computed(() => series.value?.websites.id);
const websitesLinks = computed(() => series.value?.websites?.links.nodes ?? []);

const {
    errors,
    values,
    fileUpload,
    setFieldValue,
    setFieldTouched,
    validate,
    onDiscard,
    resetForm,
    uploadIcon,
    deleteIcon,
    discardForm,
} = useForm(
    seriesIdentifiersSchema.value,
    computed(() => ({
        nameShort: series.value?.nameShort ?? '',
        nameLong: series.value?.nameLong ?? '',
        subtitle: series.value?.subtitle ?? '',
        websites: getFormFieldLinks(websitesLinks),
        numberKapman: series.value?.numberKapman ?? '',
        numberShort: series.value?.numberShort ?? '',
        foundationYear: series.value?.foundationYear ?? undefined,
        notes: series.value?.notes ?? '',
        description: series.value?.description ?? '',
        iconUpload: '',
    })),
);

const iconUrl = computed(
    () =>
        fileUpload.fileUrl.value ?? (values.value.iconUpload === '-' ? '' : thumbnailUrl(series.value?.icon, 'url512')),
);

async function save() {
    const valid = await validate();
    if (valid) {
        await updateEventSeries({ id: seriesId.value, ...values.value });
        if (values.value.iconUpload && values.value.iconUpload !== '-') {
            pendingLogoUrl.value = fileUpload.fileUrl.value;
        }
        await saveMultiLinkFields([{ id: websitesId.value, fieldName: 'websites', links: values.value.websites }]);
        resetActiveCard();
        discardForm();
    }
}

defineExpose({ save, onDiscard, resetForm });
</script>
